/**
 * Where a screen's time went — the screener's answer to the chart's `i` panel.
 *
 * A slow screen looks exactly like a fast one: the grid fills, eventually. This
 * is the surface that says *why*, and the reason it exists is that the obvious
 * two explanations are both usually wrong. Measured on qf-us, warm:
 *
 *     BIG_DATA     6 symbols      8.0s
 *     SP500      501 symbols      0.30s
 *     RU2000   1,975 symbols      0.39s
 *
 * Six symbols slower than nineteen hundred. The group is not the variable —
 * whether the request rebuilt the 15.7-million-row universe frame is, and that
 * is the one line at the top of this panel.
 *
 * Colours are `var(--qf-*)` only, like every other component in this island.
 */

import type { CSSProperties } from "react";

import { plainText } from "../lib/plain-names.js";
import { formatStageMs, type ScreenerTimings } from "../lib/timings.js";

export interface ScreenerTimingsPanelProps {
  timings: ScreenerTimings | undefined;
  onClose: () => void;
}

export function ScreenerTimingsPanel({ timings, onClose }: ScreenerTimingsPanelProps) {
  if (!timings) {
    return (
      <div style={panel}>
        <Head onClose={onClose} />
        <p style={empty}>
          This server did not send timings. They arrive as a `timings` frame on the
          group-screen route, so an older build simply omits it — which is not the
          same as a screen that took no time.
        </p>
      </div>
    );
  }

  const { stages, totalMs, unattributedMs, universe } = timings;
  const worst = stages.reduce((most, s) => Math.max(most, s.ms), 0);

  return (
    <div style={panel}>
      <Head onClose={onClose} />

      {/* The verdict, before the table. Somebody reading this panel in anger
          wants one word, and the table is what they read second. */}
      <div style={{ ...verdict, ...(universe === "rebuilt" ? verdictBad : verdictGood) }}>
        <strong>{universe === "rebuilt" ? "Universe rebuilt" : universe === "cached" ? "Universe cached" : "Universe unknown"}</strong>
        <span style={{ color: "var(--qf-text-muted)" }}>
          {universe === "rebuilt"
            ? "— 15.7M rows re-read and re-sorted for this request"
            : universe === "cached"
              ? "— the frame was reused, which is the fast path"
              : "— this server did not say"}
        </span>
        <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
          {formatStageMs(totalMs)}
        </span>
      </div>

      <table style={table}>
        <tbody>
          {stages.map((stage, i) => (
            <tr key={`${stage.name}-${i}`}>
              <td style={cellName}>{stage.name}</td>
              <td style={cellMs}>{formatStageMs(stage.ms)}</td>
              <td style={cellBar}>
                {/* Proportional to the slowest stage, not to the total: nested
                    spans make the total a poor denominator, and the question
                    this answers is "which one is the big one". */}
                <span
                  style={{
                    ...bar,
                    width: worst > 0 ? `${Math.max(1, (stage.ms / worst) * 100)}%` : "0%",
                  }}
                />
              </td>
              <td style={cellDetail} title={plainText(stage.detail)}>
                {plainText(stage.detail) ?? ""}
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cellName, color: "var(--qf-text-muted)" }}>unattributed</td>
            <td style={{ ...cellMs, color: "var(--qf-text-muted)" }}>
              {formatStageMs(unattributedMs)}
            </td>
            <td style={cellBar} />
            <td style={{ ...cellDetail, color: "var(--qf-text-muted)" }}>
              the bracket minus the parts — an un-instrumented stage shows here
            </td>
          </tr>
        </tbody>
      </table>

      <p style={foot}>
        Stages are listed innermost-first: a span is recorded when it finishes, so
        the universe rows appear <em>above</em> the <code>membership</code> that
        contains them.
      </p>
    </div>
  );
}

function Head({ onClose }: { onClose: () => void }) {
  return (
    <div style={head}>
      <strong style={{ fontSize: "0.8rem" }}>Where this screen&rsquo;s time went</strong>
      <button onClick={onClose} style={closeButton} title="Close">
        ×
      </button>
    </div>
  );
}

const panel: CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface)",
  padding: "0.5rem 0.6rem 0.6rem",
  margin: "0.5rem 0 0",
  fontSize: "0.72rem",
};

const head: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: "0.45rem",
};

const closeButton: CSSProperties = {
  marginLeft: "auto",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  lineHeight: 1,
  padding: "0.05rem 0.35rem",
};

const verdict: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0.3rem 0.45rem",
  borderRadius: 3,
  marginBottom: "0.45rem",
};

const verdictGood: CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
};

const verdictBad: CSSProperties = {
  border: "1px solid var(--qf-negative)",
  background: "var(--qf-surface-alt)",
};

const table: CSSProperties = { width: "100%", borderCollapse: "collapse" };

const cellName: CSSProperties = {
  padding: "0.12rem 0.4rem 0.12rem 0",
  whiteSpace: "nowrap",
  color: "var(--qf-text)",
};

const cellMs: CSSProperties = {
  padding: "0.12rem 0.4rem",
  textAlign: "right",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
  color: "var(--qf-text)",
};

const cellBar: CSSProperties = { padding: "0.12rem 0.4rem", width: "28%" };

const bar: CSSProperties = {
  display: "block",
  height: 6,
  borderRadius: 2,
  background: "var(--qf-accent)",
};

const cellDetail: CSSProperties = {
  padding: "0.12rem 0",
  color: "var(--qf-text-muted)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 0,
};

const empty: CSSProperties = { margin: 0, color: "var(--qf-text-muted)" };

const foot: CSSProperties = {
  margin: "0.45rem 0 0",
  color: "var(--qf-text-muted)",
  fontSize: "0.68rem",
};
